import { useState, useRef, useEffect } from 'react';
import { Check, ChevronsUpDown, Search } from 'lucide-react';

interface Option {
    value: string;
    label: string;
    rightLabel?: string;
}

interface SearchableSelectProps {
    options: Option[];
    value: string | null;
    onChange: (value: string) => void;
    placeholder?: string;
    searchPlaceholder?: string;
    disabled?: boolean;
    className?: string;
    mode?: 'select' | 'combobox';
    inputClassName?: string;
    dropdownClassName?: string;
}

export function SearchableSelect({
    options,
    value,
    onChange,
    placeholder = 'Select...',
    searchPlaceholder = 'Search...',
    disabled = false,
    className = '',
    mode = 'select',
    inputClassName = '',
    dropdownClassName = '',
}: SearchableSelectProps) {
    const [isOpen, setIsOpen] = useState(false);
    const [search, setSearch] = useState('');
    const containerRef = useRef<HTMLDivElement>(null);

    const filteredOptions = options.filter((option) =>
        option.label.toLowerCase().includes(search.toLowerCase())
    );

    const selectedLabel = options.find((opt) => opt.value === value)?.label;

    useEffect(() => {
        if (mode !== 'combobox') return;
        setSearch(selectedLabel || '');
    }, [mode, selectedLabel]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    return (
        <div className={`relative ${className}`} ref={containerRef}>
            {mode === 'combobox' ? (
                <div className={`w-full flex items-center gap-2 px-3 py-2 bg-white border rounded-md shadow-sm focus-within:ring-2 focus-within:ring-blue-500 ${disabled ? 'bg-gray-100 cursor-not-allowed text-gray-500 border-gray-200' : 'border-gray-300 text-gray-900'}`}>
                    <Search className="w-4 h-4 text-gray-400 flex-none" />
                    <input
                        type="text"
                        className={`w-full bg-transparent text-left text-sm focus:outline-none ${disabled ? 'cursor-not-allowed' : ''} ${inputClassName}`}
                        placeholder={placeholder}
                        value={search}
                        disabled={disabled}
                        onFocus={() => !disabled && setIsOpen(true)}
                        onClick={() => !disabled && setIsOpen(true)}
                        onChange={(e) => {
                            setSearch(e.target.value);
                            if (!disabled) setIsOpen(true);
                        }}
                    />
                    <ChevronsUpDown className="w-4 h-4 text-gray-400 flex-none" />
                </div>
            ) : (
                <button
                    type="button"
                    onClick={() => !disabled && setIsOpen(!isOpen)}
                    disabled={disabled}
                    className={`w-full flex items-center justify-between px-3 py-2 text-left bg-white border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${disabled ? 'bg-gray-100 cursor-not-allowed text-gray-500' : 'border-gray-300 text-gray-900'
                        }`}
                >
                    <span className="block truncate">{selectedLabel || placeholder}</span>
                    <ChevronsUpDown className="w-4 h-4 text-gray-400" />
                </button>
            )}

            {isOpen && (
                <div className={`absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-hidden flex flex-col animate-in fade-in zoom-in-95 duration-100 ${dropdownClassName}`}>
                    {mode !== 'combobox' && (
                        <div className="p-2 border-b border-gray-100">
                            <div className="relative">
                                <Search className="absolute left-2 top-2.5 w-4 h-4 text-gray-400" />
                                <input
                                    type="text"
                                    className="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-200 rounded-md focus:outline-none focus:border-blue-500"
                                    placeholder={searchPlaceholder}
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                    autoFocus
                                />
                            </div>
                        </div>
                    )}
                    <div className="overflow-y-auto flex-1">
                        {filteredOptions.length === 0 ? (
                            <div className="px-3 py-2 text-sm text-gray-500">No results found</div>
                        ) : (
                            filteredOptions.map((option) => (
                                <button
                                    key={option.value}
                                    type="button"
                                    className={`w-full flex items-center justify-between px-3 py-2 text-sm text-left hover:bg-gray-50 ${value === option.value ? 'bg-blue-50 text-blue-700' : 'text-gray-900'
                                        }`}
                                    onClick={() => {
                                        onChange(option.value);
                                        setIsOpen(false);
                                        if (mode !== 'combobox') {
                                            setSearch('');
                                        } else {
                                            setSearch(option.label);
                                        }
                                    }}
                                >
                                    <span className="flex items-center justify-between w-full min-w-0">
                                        <span className="truncate">{option.label}</span>
                                        {option.rightLabel && (
                                            <span className={`ml-3 text-xs font-semibold tabular-nums ${value === option.value ? 'text-blue-600' : 'text-gray-400'}`}>
                                                {option.rightLabel}
                                            </span>
                                        )}
                                    </span>
                                    {value === option.value && <Check className="w-4 h-4" />}
                                </button>
                            ))
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
